import React from 'react';
import { noop } from 'lodash';
import { isFeatureAllowedForICPAccount } from 'modules/accounts/utils/isFeatureAllowedForICPAccount';
import { SuggestionContentTitle } from 'modules/campaigns/components/SuggestionContentTitle/SuggestionContentTitle';
import {
    creativeDescriptionValidations,
    creativeTitleValidations,
    maxCreativeTitleLength,
    TITLE_WARNING_THRESHOLD,
    urlValidations,
} from 'modules/campaigns/config';
import { deleteCreative } from 'modules/campaigns/flows';
import { setSelectedRows } from 'modules/campaigns/modules/campaigns-reports/actions';
import {
    CheckboxCellRenderer,
    CheckboxHeaderRenderer,
    ContentCellRenderer,
    CreativeActionsCellRenderer,
    CreativeIdCellRenderer,
    CreativeStatusRenderer,
    DummyBidModificationCellRenderer,
    EllipsisCellRenderer,
    FormattedMessageHeaderRenderer,
    FormattedMessageWithTooltipHeaderRenderer,
    formattedNumberValueGetter,
    formattedUTCDateValueGetter,
    gridNumberFormatter,
    gridUTCDateFormatter,
    IMAGE_CELL_RENDERER_MODES,
    ImageCellRenderer,
    RssBulkDisabledTooltip,
    StateCellRenderer,
    StoppedCreativeBulkDisabledTooltip,
    withCreativeEditIndication,
    ReachHeaderRenderer,
} from 'modules/campaigns/modules/campaigns-reports/components';
import { PerformanceRuleCellEditor } from 'modules/campaigns/modules/campaigns-reports/components/CellEditors/PerformanceRuleCellEditor';
import {
    actionsColumnType,
    bypassMetadataFilterType,
    checkBoxSelectionColumn,
    editableColumn,
    keyColumn,
    leftPinnedColumn,
    lockedColumn,
    numericColumn,
    rightPinnedColumn,
    staticColumn,
    toggleColumn,
} from 'modules/campaigns/modules/campaigns-reports/config/columns';
import * as FIELDS from 'modules/campaigns/modules/campaigns-reports/config/fields';
import {
    isLibraryCreative,
    isCreativeRejected,
    isCreativeStatusEditable,
    isCreativeStopped,
    isHTMLCardCreative,
    isRssChild,
    onlyNetworkColumn,
    isCreativeResubmittable,
    hasResubmittableData,
    isCreativeLibraryEnabled,
    isAdRemoved,
} from 'modules/campaigns/modules/campaigns-reports/config/predicates';
import {
    duplicateCreative,
    editCreative,
    previewCreative,
    saveCreative,
    saveCreativeUrlCellEditor,
    scheduleCreative,
} from 'modules/campaigns/modules/campaigns-reports/flows';
import { normalizeColumnDefs } from 'modules/campaigns/modules/campaigns-reports/utils';
import { isCarouselCreative } from 'modules/campaigns/modules/creative-creator/components/CarouselCardList/utils/isCarouselCreative';
import {
    DESCRIPTION_WARNING_THRESHOLD,
    MAX_DESCRIPTION_LENGTH,
} from 'modules/campaigns/modules/creative-creator/components/ContentField/config';
import { BYPASS_URL_RESPONSE_VALIDATION_FIELD } from 'modules/campaigns/modules/creative-creator/components/UrlList/UrlValidatedInput';
import { PERFORMANCE_RECOMMENDATIONS } from 'modules/campaigns/modules/performance-recommendations/config';
import { FEATURE_FLAGS } from 'modules/taboola-common-frontend-modules/account-configurations';
import { PERMISSIONS_OPERATORS } from 'modules/taboola-common-frontend-modules/authentication';
import { CREATIVE_REPORT_ID } from './creativeReportId';

const isCreativeRemoved = data => isCreativeStopped(data) || isAdRemoved({ data });

const isDisabled = data => {
    return isRssChild(data) || isCreativeRemoved(data);
};
const tooltipContent = data =>
    (isRssChild(data) && <RssBulkDisabledTooltip />) ||
    (isCreativeRemoved(data) && <StoppedCreativeBulkDisabledTooltip />) ||
    null;
const isEditable = ({ data, context, shouldCheckDisplay }) => {
    const libraryCreative = isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig);

    const resubmissionAllowed = !hasResubmittableData(data) || isCreativeResubmittable(data);

    const baseCheck = !libraryCreative && resubmissionAllowed && !isCreativeRemoved(data);

    return shouldCheckDisplay ? baseCheck && !isHTMLCardCreative(data) : baseCheck;
};

const creativeReportColumnDefinition = [
    {
        headerComponent: CheckboxHeaderRenderer,
        headerName: FIELDS.IS_SELECTED.label,
        field: FIELDS.IS_SELECTED.field,
        type: [leftPinnedColumn, checkBoxSelectionColumn, staticColumn],
        cellRendererParams: {
            onClick: setSelectedRows,
            isDisabled,
            tooltipContent,
        },
        headerComponentParams: {
            onClick: setSelectedRows,
            isDisabled: data => isDisabled(data) || isCarouselCreative(data),
            tooltipContent,
        },
        valueGetter: FIELDS.IS_SELECTED.valueGetter,
        cellRenderer: CheckboxCellRenderer,
        csv: false,
        permissions: {
            permissions: ['CAMPAIGNS_ITEMS_BULK_UPDATE'],
        },
        accountConfigurations: {
            [FEATURE_FLAGS.CREATIVES_BULK_ACTIONS_ENABLED]: 'true',
        },
    },
    {
        headerName: FIELDS.IS_ACTIVE.label,
        field: FIELDS.IS_ACTIVE.field,
        type: [lockedColumn, toggleColumn, leftPinnedColumn, staticColumn],
        cellRendererParams: {
            ...FIELDS.IS_ACTIVE.renderParams,
            isDisabled: data => isCreativeRemoved(data) || isCreativeRejected(data) || isCarouselCreative(data),
            reportId: CREATIVE_REPORT_ID,
        },
        valueChangeHandler: saveCreative,
        cellRenderer: StateCellRenderer,
        csv: false,
    },
    {
        headerName: FIELDS.CUSTOM_CREATIVE_NAME.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CUSTOM_CREATIVE_NAME.headerComponentParams,
        field: FIELDS.CUSTOM_CREATIVE_NAME.field,
        type: [leftPinnedColumn, editableColumn],
        editable: ({ data, context }) =>
            !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig)),
        cellRenderer: EllipsisCellRenderer,
        cellRendererParams: { lines: 2 },
        cellEditor: FIELDS.CUSTOM_CREATIVE_NAME.cellEditor,
        valueChangeHandler: saveCreative,
        valueFormatter: FIELDS.CUSTOM_CREATIVE_NAME.valueFormatter,
        accountConfigurations: { [FEATURE_FLAGS.SHOW_CUSTOM_DATA_IN_CREATIVE_FORM]: 'true' },
    },
    {
        headerName: FIELDS.CUSTOM_ID.label,
        field: FIELDS.CUSTOM_ID.field,
        type: [leftPinnedColumn, editableColumn],
        editable: ({ data, context }) =>
            !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig)),
        cellRenderer: EllipsisCellRenderer,
        cellRendererParams: { lines: 2 },
        cellEditor: FIELDS.CUSTOM_ID.cellEditor,
        valueChangeHandler: saveCreative,
        valueFormatter: FIELDS.CUSTOM_CREATIVE_NAME.valueFormatter,
        accountConfigurations: { [FEATURE_FLAGS.SHOW_CUSTOM_DATA_IN_CREATIVE_FORM]: 'true' },
    },
    {
        headerName: FIELDS.CAMPAIGNS_GROUP_ID.label,
        field: FIELDS.CAMPAIGNS_GROUP_ID.field,
        type: [numericColumn],
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CAMPAIGNS_GROUP_ID.headerComponentParams,
        accountConfigurations: { [FEATURE_FLAGS.CAMPAIGN_GROUP_ENABLED]: 'true' },
    },
    {
        headerName: FIELDS.CAMPAIGNS_GROUP_NAME.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CAMPAIGNS_GROUP_NAME.headerComponentParams,
        field: FIELDS.CAMPAIGNS_GROUP_NAME.field,
        cellRenderer: FIELDS.CAMPAIGNS_GROUP_NAME.cellRenderer,
        cellRendererParams: FIELDS.CAMPAIGNS_GROUP_NAME.cellRenderer,
        accountConfigurations: { [FEATURE_FLAGS.CAMPAIGN_GROUP_ENABLED]: 'true' },
    },
    {
        headerName: FIELDS.BID_MODIFICATION.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.BID_MODIFICATION.headerComponentParams,
        field: FIELDS.BID_MODIFICATION.field,
        type: [bypassMetadataFilterType],
        cellRenderer: DummyBidModificationCellRenderer,
        width: 150,
        csvValueGetter: noop,
        permissions: {
            permissions: ['ADS_REPORT_DUMMY_BID_ADJUSTMENT_VIEW'],
            operator: PERMISSIONS_OPERATORS.AND,
        },
    },
    {
        headerName: FIELDS.AD_ID.label,
        field: FIELDS.AD_ID.field,
        type: [numericColumn],
        cellRenderer: CreativeIdCellRenderer,
        headerValueGetter: FormattedMessageHeaderRenderer,
        cellRendererParams: { isSearchable: true },
        valueGetter: ({ data }) => {
            const { isCreativeLibrary, adId: linkId, id: instructionId } = data;
            return isCreativeLibrary ? linkId : instructionId;
        },
        csvValueGetter: FIELDS.AD_ID.csvValueGetter,
    },
    {
        headerName: FIELDS.CREATIVE_ID.label,
        field: FIELDS.CREATIVE_ID.field,
        type: [numericColumn],
        cellRenderer: CreativeIdCellRenderer,
        headerValueGetter: FormattedMessageHeaderRenderer,
        cellRendererParams: { isSearchable: true },
        valueGetter: ({ data }) => {
            const { isCreativeLibrary, id: instructionId } = data;
            return isCreativeLibrary ? instructionId : null;
        },
        visibilityCondition: ({ selectedAccountConfig }) => isCreativeLibraryEnabled(selectedAccountConfig),
    },
    {
        headerName: FIELDS.THUMBNAIL_URL.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.TITLE.headerComponentParams,
        field: FIELDS.THUMBNAIL_URL.field,
        type: [leftPinnedColumn, editableColumn],
        editable: ({ data, context }) =>
            !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig)),
        cellRenderer: ImageCellRenderer,
        cellRendererParams: {
            mode: IMAGE_CELL_RENDERER_MODES.DEFAULT,
            isEditable: ({ data, context }) => isEditable({ data, context, shouldCheckDisplay: false }),
        },
        onCellClicked: ({ data, context }) => {
            if (!(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig))) {
                return editCreative({ data, context });
            }
        },
        singleClickEdit: false,
        width: 120,
        csv: FIELDS.THUMBNAIL_URL.csv,
        visibilityCondition: ({ isComfortableViewEnabled }) => !isComfortableViewEnabled,
    },
    {
        headerName: FIELDS.THUMBNAIL_URL_COMFORTABLE.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.THUMBNAIL_URL_COMFORTABLE.headerComponentParams,
        field: FIELDS.THUMBNAIL_URL_COMFORTABLE.field,
        type: [leftPinnedColumn, editableColumn, bypassMetadataFilterType],
        editable: ({ data, context }) =>
            !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig)),
        valueGetter: ({ data }) => {
            const { thumbnailUrl } = data;
            return thumbnailUrl;
        },
        cellRenderer: ImageCellRenderer,
        cellRendererParams: {
            mode: IMAGE_CELL_RENDERER_MODES.COMFORTABLE,
            isEditable: ({ data, context }) => isEditable({ data, context, shouldCheckDisplay: false }),
        },
        onCellClicked: ({ data, context }) => {
            if (!(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig))) {
                return editCreative({ data, context });
            }
        },
        singleClickEdit: false,
        width: 186,
        csv: FIELDS.THUMBNAIL_URL_COMFORTABLE.csv,
        visibilityCondition: ({ isComfortableViewEnabled }) => isComfortableViewEnabled,
    },
    {
        headerName: FIELDS.CONTENT.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CONTENT.headerComponentParams,
        type: [editableColumn, bypassMetadataFilterType],
        valueGetter: ({ data }) => {
            const { title, description, cta } = data;
            return {
                title,
                description,
                cta,
            };
        },
        field: FIELDS.CONTENT.field,
        cellRenderer: ContentCellRenderer,
        cellRendererParams: {
            lines: 2,
            ctaMsgIdPrefix: 'creative.creator.content.cta.option',
            isSearchable: true,
            isEditable: ({ data, context }) => isEditable({ data, context, shouldCheckDisplay: true }),
        },
        onCellClicked: ({ data, context }) => {
            if (
                !isHTMLCardCreative(data) &&
                !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig))
            ) {
                return editCreative({ data, context });
            }
        },
        singleClickEdit: false,
        width: 328,
        csv: [
            {
                headerName: 'Title',
                field: 'title',
            },
            {
                headerName: 'Description',
                field: 'description',
            },
            ...FIELDS.CTA.csv,
        ],
        visibilityCondition: ({ isComfortableViewEnabled }) => isComfortableViewEnabled,
        editable: ({ data, context }) =>
            !isHTMLCardCreative(data) &&
            !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig)),
    },
    {
        headerName: FIELDS.TITLE.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.TITLE.headerComponentParams,
        field: FIELDS.TITLE.field,
        type: [keyColumn, editableColumn],
        editable: ({ data, context }) =>
            !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig)),
        cellRenderer: EllipsisCellRenderer,
        cellRendererParams: {
            lines: 2,
            isSearchable: true,
        },
        cellEditor: FIELDS.TITLE.cellEditor,
        cellEditorParams: {
            validations: creativeTitleValidations,
            maxLength: maxCreativeTitleLength,
            warningThreshold: TITLE_WARNING_THRESHOLD,
            EditableTextComponent: SuggestionContentTitle,
            editableTextClassName: 'overflow-visible',
        },
        valueChangeHandler: saveCreative,
        visibilityCondition: ({ isComfortableViewEnabled }) => !isComfortableViewEnabled,
    },
    {
        headerName: FIELDS.CHANNEL_TYPE.label,
        field: FIELDS.CHANNEL_TYPE.field,
        cellRendererParams: FIELDS.CHANNEL_TYPE.renderParams,
        cellRenderer: FIELDS.CHANNEL_TYPE.cellRenderer,
        csvValueGetter: FIELDS.CHANNEL_TYPE.csvValueGetter,
        visibilityCondition: ({ selectedAccount, selectedAccountConfig }) =>
            isFeatureAllowedForICPAccount(
                selectedAccount,
                selectedAccountConfig,
                FEATURE_FLAGS.SHOULD_SHOW_DISPLAY_DATA
            ),
    },
    {
        headerName: FIELDS.CREATIVE_ORIENTATION.label,
        field: FIELDS.CREATIVE_ORIENTATION.field,
        cellRendererParams: FIELDS.CREATIVE_ORIENTATION.renderParams,
        cellRenderer: FIELDS.CREATIVE_ORIENTATION.cellRenderer,
        csvValueGetter: FIELDS.CREATIVE_ORIENTATION.csvValueGetter,
    },
    {
        headerName: FIELDS.FORMAT.label,
        field: FIELDS.FORMAT.field,
        cellRendererParams: FIELDS.FORMAT.renderParams,
        cellRenderer: FIELDS.FORMAT.cellRenderer,
        csvValueGetter: FIELDS.FORMAT.csvValueGetter,
        valueGetter: ({ data }) => {
            const { format } = data;
            if (isHTMLCardCreative(data)) {
                return data.displayData.displayAdType;
            }
            return format;
        },
        visibilityCondition: ({ selectedAccount, selectedAccountConfig }) =>
            isFeatureAllowedForICPAccount(
                selectedAccount,
                selectedAccountConfig,
                FEATURE_FLAGS.SHOULD_SHOW_DISPLAY_DATA
            ),
    },
    {
        headerName: FIELDS.SIZE.label,
        field: FIELDS.SIZE.field,
        cellRenderer: FIELDS.SIZE.cellRenderer,
        valueFormatter: FIELDS.SIZE.valueFormatter,
        valueGetter: FIELDS.SIZE.valueGetter,
        csv: FIELDS.SIZE.csv,
        visibilityCondition: ({ selectedAccount, selectedAccountConfig }) =>
            isFeatureAllowedForICPAccount(
                selectedAccount,
                selectedAccountConfig,
                FEATURE_FLAGS.SHOULD_SHOW_DISPLAY_DATA
            ),
    },
    {
        headerName: FIELDS.URL.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.URL.headerComponentParams,
        field: FIELDS.URL.field,
        type: [editableColumn, keyColumn],
        editable: ({ data, context }) =>
            !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig)),
        cellRenderer: FIELDS.URL.cellRenderer,
        cellEditor: FIELDS.URL.cellEditor,
        cellEditorParams: {
            validations: urlValidations,
        },
        valueChangeHandler: saveCreativeUrlCellEditor,
        cellRendererParams: { isSearchable: true },
        valueSetter: params => {
            params.data[FIELDS.URL.field] = params.newValue.url;
            params.data[BYPASS_URL_RESPONSE_VALIDATION_FIELD] = params.newValue[BYPASS_URL_RESPONSE_VALIDATION_FIELD];
            return true;
        },
    },
    {
        headerName: FIELDS.DESCRIPTION.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.DESCRIPTION.headerComponentParams,
        field: FIELDS.DESCRIPTION.field,
        type: [editableColumn],
        editable: ({ data, context }) =>
            !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig)),
        cellRenderer: EllipsisCellRenderer,
        width: 270,
        cellEditor: FIELDS.DESCRIPTION.cellEditor,
        cellEditorParams: {
            validations: creativeDescriptionValidations,
            maxLength: MAX_DESCRIPTION_LENGTH,
            warningThreshold: DESCRIPTION_WARNING_THRESHOLD,
        },
        valueChangeHandler: saveCreative,
        cellRendererParams: { isSearchable: true },
        visibilityCondition: ({ isComfortableViewEnabled }) => !isComfortableViewEnabled,
    },
    {
        headerName: FIELDS.STATUS.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.STATUS.headerComponentParams,
        field: FIELDS.STATUS.field,
        type: [leftPinnedColumn, editableColumn],
        cellRenderer: CreativeStatusRenderer,
        flex: 1,
        minWidth: 200,
        editable: ({ data, context }) =>
            isCreativeStatusEditable({ data, context }) &&
            !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig)),
        cellEditor: PerformanceRuleCellEditor,
        cellRendererParams: {
            ...FIELDS.STATUS.renderParams,
            recommendationTypes: [
                PERFORMANCE_RECOMMENDATIONS.LOW_PERFORMING_ITEM,
                PERFORMANCE_RECOMMENDATIONS.LOW_RESOLUTION_IC,
                PERFORMANCE_RECOMMENDATIONS.ITEM_WITHOUT_DESCRIPTION,
                PERFORMANCE_RECOMMENDATIONS.ITEM_WITHOUT_CTA,
                PERFORMANCE_RECOMMENDATIONS.FATIGUED_ITEM,
            ],
        },
        csvValueGetter: FIELDS.STATUS.csvValueGetter,
    },
    {
        headerName: FIELDS.REACH.label,
        headerValueGetter: ReachHeaderRenderer,
        headerComponentParams: FIELDS.REACH.headerComponentParams,
        field: FIELDS.REACH.field,
        minWidth: 200,
        cellRenderer: FIELDS.REACH.cellRenderer,
        cellRendererParams: FIELDS.REACH.renderParams,
    },
    {
        headerName: FIELDS.ACCOUNT_ID.label,
        field: FIELDS.ACCOUNT_ID.field,
        visibilityCondition: onlyNetworkColumn,
    },
    {
        headerName: FIELDS.ACCOUNT_DESCRIPTION.label,
        field: FIELDS.ACCOUNT_DESCRIPTION.field,
        visibilityCondition: onlyNetworkColumn,
    },
    {
        headerName: FIELDS.CTA.label,
        field: FIELDS.CTA.field,
        valueGetter: FIELDS.CTA.valueGetter,
        type: [editableColumn],
        editable: ({ data, context }) =>
            !(isLibraryCreative({ data }) && isCreativeLibraryEnabled(context?.selectedAccountConfig)),
        cellRenderer: FIELDS.CTA.cellRenderer,
        cellRendererParams: FIELDS.CTA.renderParams,
        cellEditor: FIELDS.CTA.cellEditor,
        valueChangeHandler: saveCreative,
        csv: FIELDS.CTA.csv,
        visibilityCondition: ({ isComfortableViewEnabled }) => !isComfortableViewEnabled,
    },
    {
        headerName: FIELDS.CAMPAIGN_ID.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.CAMPAIGN_ID.headerComponentParams,
        field: FIELDS.CAMPAIGN_ID.field,
        type: [numericColumn],
        cellRenderer: EllipsisCellRenderer,
    },
    {
        headerName: FIELDS.CAMPAIGN_NAME.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.STATUS.headerComponentParams,
        field: FIELDS.CAMPAIGN_NAME.field,
        cellRenderer: FIELDS.CAMPAIGN_NAME.cellRenderer,
        cellRendererParams: { isSearchable: true },
    },
    {
        headerName: FIELDS.START_DATE.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.START_DATE.headerComponentParams,
        field: FIELDS.START_DATE.field,
        valueFormatter: gridUTCDateFormatter,
        csvValueGetter: formattedUTCDateValueGetter,
    },
    {
        headerName: FIELDS.END_DATE.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.END_DATE.headerComponentParams,
        field: FIELDS.END_DATE.field,
        valueFormatter: gridUTCDateFormatter,
        csvValueGetter: formattedUTCDateValueGetter,
    },
    {
        headerName: FIELDS.CREATE_TIME.label,
        headerValueGetter: FormattedMessageHeaderRenderer,
        headerComponentParams: FIELDS.STATUS.headerComponentParams,
        field: FIELDS.CREATE_TIME.field,
        valueFormatter: FIELDS.CREATE_TIME.valueFormatter,
        csvValueGetter: FIELDS.CREATE_TIME.csvValueGetter,
    },
    {
        headerName: FIELDS.ACTIONS_COLUMN.label,
        field: FIELDS.ACTIONS_COLUMN.field,
        type: [rightPinnedColumn, actionsColumnType, staticColumn],
        minWidth: 150,
        maxWidth: 150,
        width: 150,
        cellRendererParams: {
            onScheduler: scheduleCreative,
            onDelete: deleteCreative,
            onPreview: previewCreative,
            onEdit: editCreative,
            onDuplicate: duplicateCreative,
        },
        cellRenderer: CreativeActionsCellRenderer,
        csv: false,
    },
    ...[
        FIELDS.VISIBLE_IMPRESSIONS,
        FIELDS.VCTR,
        FIELDS.CLICKS,
        FIELDS.AVERAGE_CPC,
        FIELDS.CVR,
        FIELDS.CVR_CLICKS,
        FIELDS.CVR_VIEWS,
        FIELDS.CONVERSIONS,
        FIELDS.CONVERSIONS_CLICKS,
        FIELDS.CONVERSIONS_VIEWS,
        FIELDS.CPA,
        FIELDS.CPA_CLICKS,
        FIELDS.CPA_VIEWS,
        FIELDS.VCPM,
        FIELDS.IMPRESSIONS,
        FIELDS.SPENT,
        FIELDS.CONVERSIONS_VALUE,
        FIELDS.ROAS,
        FIELDS.ROAS_CLICKS,
        FIELDS.ROAS_VIEWS,
    ].map(fieldConfig => ({
        headerName: fieldConfig.label,
        headerValueGetter: fieldConfig.headerValueGetter || FormattedMessageWithTooltipHeaderRenderer,
        headerComponentParams: fieldConfig.headerComponentParams,
        field: fieldConfig.field,
        type: [numericColumn],
        valueFormatter: gridNumberFormatter,
        cellRendererParams: fieldConfig.renderParams,
        csvValueGetter: fieldConfig.csvValueGetter || formattedNumberValueGetter,
        referenceColId: fieldConfig.referenceColId,
        permissions: fieldConfig.permissions,
        visibilityCondition: fieldConfig.visibilityCondition,
    })),
];

export default normalizeColumnDefs(creativeReportColumnDefinition, CREATIVE_REPORT_ID, withCreativeEditIndication);
